import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { Building2, ArrowRightLeft, ScrollText, Check } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { GatedDownload } from "@/components/site/GatedDownload";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Primitives";
import { Counter } from "@/components/site/Counter";
import api from "@/lib/api";

export default function MarketDetail() {
  const { slug } = useParams();
  const [market, setMarket] = useState(undefined);

  useEffect(() => {
    setMarket(undefined);
    api.getMarket(slug).then(setMarket).catch(() => setMarket(null));
  }, [slug]);

  if (market === null) return <Navigate to="/markets" replace />;
  if (!market) return <div className="min-h-screen" />;

  return (
    <div data-testid="market-detail-page">
      <PageHero
        breadcrumb={[{ label: "Markets", to: "/markets" }, { label: market.name }]}
        eyebrow={`${market.name} · ${market.currency}`}
        title={market.tagline}
        intro={market.intro}
        image={market.image}
      />

      {/* Stats */}
      <section className="border-y border-[color:var(--bp-border)]/50 bg-[color:var(--bp-bg-2)] py-12">
        <div className="container-bp grid grid-cols-1 gap-8 sm:grid-cols-3">
          {market.stats.map((s) => (
            <Reveal key={s.label}>
              <div className="text-center sm:text-left">
                <p className="font-display text-4xl font-semibold text-[color:var(--bp-gold)]">{s.value}</p>
                <p className="mt-2 text-[14px] text-[color:var(--bp-text-muted)]">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Demand chart + segments */}
      <section className="section-py">
        <div className="container-bp grid grid-cols-1 gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Hiring demand" title={`What ${market.name} is hiring now.`} intro="Relative demand index across the segments we cover, updated from our live mandate flow." />
            <div className="mt-8 h-[300px] w-full rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-5" data-testid="market-demand-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={market.chart} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid horizontal={false} stroke="#144970" strokeOpacity={0.4} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#C5BFB6" }} domain={[0, 100]} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: "#C5BFB6" }} width={92} />
                  <Tooltip cursor={{ fill: "rgba(45,181,165,0.08)" }} contentStyle={{ background: "#0D2B4D", border: "1px solid #144970", borderRadius: 10, color: "#F5F1EB" }} />
                  <Bar dataKey="demand" radius={[0, 6, 6, 0]}>
                    {market.chart.map((_, i) => <Cell key={i} fill={i === 0 ? "#D4AF37" : "#2DB5A5"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <SectionHeading eyebrow="Active segments" title="Where the mandates are." />
            <RevealGroup className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {market.segments.map((seg) => (
                <RevealItem key={seg}>
                  <div className="flex items-center gap-3 rounded-[var(--radius-md)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-4">
                    <Check className="h-4 w-4 shrink-0 text-[color:var(--bp-teal)]" />
                    <span className="text-[14px] text-[color:var(--bp-cream)]">{seg}</span>
                  </div>
                </RevealItem>
              ))}
            </RevealGroup>
            <div className="mt-6 grid grid-cols-1 gap-4">
              <Reveal>
                <div className="rounded-[var(--radius-md)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-5">
                  <p className="flex items-center gap-2 font-display text-base font-semibold text-[color:var(--bp-cream)]"><ScrollText className="h-4 w-4 text-[color:var(--bp-teal)]" /> Regulatory context</p>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--bp-text-muted)]">{market.regulatory}</p>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <div className="rounded-[var(--radius-md)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-5">
                  <p className="flex items-center gap-2 font-display text-base font-semibold text-[color:var(--bp-cream)]"><ArrowRightLeft className="h-4 w-4 text-[color:var(--bp-teal)]" /> Talent mobility</p>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[color:var(--bp-text-muted)]">{market.mobility}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Gated report */}
      <section className="section-py bg-[color:var(--bp-bg-2)]">
        <div className="container-bp grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <SectionHeading eyebrow="Proprietary report" title={`The ${market.name} Talent & Compensation Report 2026.`} intro="Role-by-role compensation benchmarks, hiring velocity and talent-flow analysis — free for qualified institutions." />
          <GatedDownload title={`Download the ${market.name} report`} description="Enter your work email and we'll send the full report." resource={`${market.slug}-report`} />
        </div>
      </section>

      <CTABand title={`Hiring in ${market.name}? Let's talk.`} />
    </div>
  );
}
